import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	ArrowLeftIcon,
	Button,
	Container,
	Empty,
	Heading,
	Heading3,
	Separator,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import DepartmentDetails from './DepartmentDetails';
import DepartmentToolbar from './DepartmentToolbar';
import GroupMember from './GroupMember';

const Grid = styled(Box)`
	display: grid;
	max-width: 560px;
	grid-template-columns: repeat(auto-fit, 180px);
	gap: 10px;
`;

export default function Group(props: { id: string }) {
	const { t } = useTranslation();

	const query = graphql.useGroupQuery({ variables: { id: props.id, first: 10000 } });

	return (
		<Container pt={`${tokens.gap.page}px`} pb={4}>
			<Flex mb={3} ml="-16px">
				<Button
					icon={<ArrowLeftIcon />}
					title={t('common.back')}
					type="transparent"
					link="/settings/organization/departments"
				/>
			</Flex>
			<Flex alignItems="center" mb={`${tokens.gap.group}px`}>
				<Heading flexGrow={1} flexShrink={1} mb={0}>
					{t('settings.departments.department.title')}
				</Heading>
				{query.data ? (
					<DepartmentToolbar group={query.data.group as graphql.Group} />
				) : (
					<Button.Skeleton />
				)}
			</Flex>
			<Flex mb={4}>
				<Box flexBasis={0} flexGrow={1} flexShrink={1} mr={2}>
					{query.data ? (
						<DepartmentDetails group={query.data.group as graphql.Group} />
					) : (
						<DepartmentDetails.Skeleton />
					)}
				</Box>
				<Box flexBasis={0} flexGrow={1} flexShrink={1} ml={2}>
					<Flex alignItems="center" mb={3}>
						<Heading3 mb={0}>{t('settings.departments.department.managers.title')}:</Heading3>
					</Flex>
					{query.data ? (
						query.data.group.managers.edges.length === 0 ? (
							<Box width={180 * 3 + 20}>
								<Skeleton.Empty
									contents={
										<Empty
											small
											description={t('settings.departments.department.managers.empty')}
										/>
									}
								>
									<Grid>
										<Skeleton.Repeat count={3}>
											<GroupMember.Skeleton />
										</Skeleton.Repeat>
									</Grid>
								</Skeleton.Empty>
							</Box>
						) : (
							<Grid>
								{query.data.group.managers.edges.map((edge) => (
									<GroupMember
										key={edge.node.id}
										user={edge.node as graphql.User}
										role={edge.role as graphql.Role}
									/>
								))}
							</Grid>
						)
					) : (
						<Grid>
							<Skeleton.Repeat count={3}>
								<GroupMember.Skeleton />
							</Skeleton.Repeat>
						</Grid>
					)}
				</Box>
			</Flex>

			<Box mb={4}>
				<Separator />
				<Heading3 mt={`${tokens.gap.section}px`} mb={3}>
					{t('settings.departments.department.members.title')}:
				</Heading3>
				{query.data ? (
					query.data.group.members.edges.length === 0 ? (
						<Box width={180 * 3 + 20}>
							<Skeleton.Empty
								contents={
									<Empty small description={t('settings.departments.department.members.empty')} />
								}
							>
								<Grid>
									<Skeleton.Repeat count={3}>
										<GroupMember.Skeleton />
									</Skeleton.Repeat>
								</Grid>
							</Skeleton.Empty>
						</Box>
					) : (
						<Grid>
							{_.sortBy(
								query.data?.group.members.edges,
								(edge) => `${edge.node.firstName} ${edge.node.lastName}`,
							).map((edge) => (
								<GroupMember
									key={edge.node.id}
									user={edge.node as graphql.User}
									role={
										{
											id: 'group-member',
											type: graphql.RoleType.Member,
										} as graphql.Role
									}
								/>
							))}
						</Grid>
					)
				) : (
					<Grid>
						<Skeleton.Repeat count={3}>
							<GroupMember.Skeleton />
						</Skeleton.Repeat>
					</Grid>
				)}
			</Box>
		</Container>
	);
}
